import { Injectable, type Provider } from '@angular/core';

import { err, makeError, ok } from '../../core/models/api-result';
import { myResponseOf, type HubEventResponse, type HubEventView } from '../hubs/events/hub-event.vm';
import { ORG_EVENT_NEW_ARGS, OrgEventsService } from './org-events.service';
import {
  ORG_EVENT_RANGES,
  ORG_EVENTS_PAGE_SIZE,
  type OrgEventsFilters,
} from './org-events.vm';

/**
 * ORGANISATION EVENTS WITHOUT A SESSION — the `/preview/events` seam.
 *
 * The harness answers the same filters the server does, over
 * `org-events-preview.fixtures.ts`: tab, hub, office, kinds and range, then
 * the page. Replies change the harness's copy only. `?empty=1` serves nothing,
 * `?slow=1` holds the read for a few seconds, so the empty state and the
 * skeleton can be reviewed, and `?old=1` plays an API that lacks the filters,
 * paging and My replies, so the page's calm note can be.
 */
@Injectable()
export class OrgEventsPreviewService extends OrgEventsService {
  private readonly opened = new Date();
  private store: HubEventView[] | null = null;

  private async fixtures(): Promise<HubEventView[]> {
    if (!this.store) {
      const { previewOrgEvents } = await import('./org-events-preview.fixtures');
      this.store = previewOrgEvents(this.opened);
    }
    return this.store;
  }

  /** The harness answers every argument itself; `?old=1` plays an API from before them. */
  protected override async fetchArgNames(): Promise<ReadonlySet<string> | null> {
    const search = typeof location === 'undefined' ? '' : location.href;
    return /[?&]old=1/.test(search) ? new Set<string>() : new Set(ORG_EVENT_NEW_ARGS.map(([name]) => name));
  }

  protected override async fetchPage(filters: OrgEventsFilters, now: Date, offset: number) {
    const search = typeof location === 'undefined' ? '' : location.href;
    if (/[?&]slow=1/.test(search)) await new Promise(resolve => setTimeout(resolve, 4000));
    if (/[?&]empty=1/.test(search)) return ok<HubEventView[] | null>([]);
    const days = ORG_EVENT_RANGES.find(r => r.id === filters.range)?.days ?? null;
    const horizon = days === null ? Infinity : now.getTime() + days * 86_400_000;
    const rows = (await this.fixtures())
      .filter(e => new Date(e.endsAt ?? e.startsAt).getTime() >= now.getTime())
      .filter(e => new Date(e.startsAt).getTime() <= horizon)
      .filter(e => !filters.hubId || e.communityId === filters.hubId)
      .filter(e => !filters.locationId || e.venues.some(v => v.locationId === filters.locationId))
      .filter(e => filters.kinds.length === 0 || filters.kinds.includes((e.kind ?? '') as never))
      .filter(
        e => filters.tab !== 'mine' || ['GOING', 'INTERESTED'].includes(myResponseOf(e) ?? ''),
      )
      .sort((a, b) => new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime());
    return ok<HubEventView[] | null>(rows.slice(offset, offset + ORG_EVENTS_PAGE_SIZE));
  }

  protected override async sendRsvp(eventId: string, response: HubEventResponse) {
    const all = await this.fixtures();
    const i = all.findIndex(e => e.id === eventId);
    if (i < 0) return err(makeError('NOT_FOUND', 'Event not found'));
    const event = all[i];
    if (
      response === 'GOING' &&
      (event.capacity ?? 0) > 0 &&
      (event.goingCount ?? 0) >= (event.capacity ?? 0) &&
      myResponseOf(event) !== 'GOING'
    ) {
      return err(makeError('CONFLICT', 'This event is at capacity'));
    }
    all[i] = { ...event, myResponse: response };
    return ok<Partial<HubEventView> | null>({ id: eventId, myResponse: response });
  }
}

/** Drop into a route's `providers` to serve the harness instead of the API. */
export const ORG_EVENTS_PREVIEW_PROVIDER: Provider = {
  provide: OrgEventsService,
  useClass: OrgEventsPreviewService,
};
